import { DISPLAY_ONLY_FIELD_TYPES, FLOW_CONDITION_FIELDS, } from '../types.js';
/** Hard upper bounds enforced regardless of the per-field configuration. */
export const LIMITS = {
    text: 200,
    textarea: 2000,
    phone: 40,
    email: 254,
    option: 128,
    multiselectItems: 25,
    fieldsPerForm: 60,
    numberMin: -1_000_000_000,
    numberMax: 1_000_000_000,
};
/* ------------------------------------------------------------------- helpers */
/**
 * Deliberately permissive e-mail check. Strict RFC 5322 validation rejects
 * addresses that real mail servers accept; the goal here is to catch typos at
 * a reception desk, not to be a parser.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
/** Digits, spaces and the usual separators; at least 5 digits overall. */
const PHONE_ALLOWED_RE = /^[+()\-./\s\d]+$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Removes control characters, zero-width and bidi control marks, normalises
 * line endings and collapses runs of spaces. Tabs and newlines survive here so
 * that a multi-line "Anliegen" keeps its structure; single-line fields are put
 * through normalizeSingleLine afterwards.
 *
 * Applied to every string that reaches the database, so neither a chat message
 * nor a CSV export can be broken by pasted input.
 */
export function normalizeText(input) {
    return (input
        .replace(/\r\n?/g, '\n')
        // Stripping control characters is the entire point of this line, so the
        // rule that objects to them in a pattern has nothing useful to say.
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000B-\u001F\u007F-\u009F\u200B-\u200F\u2028\u2029\u202A-\u202E\uFEFF]/g, '')
        .replace(/[ \t]+/g, ' ')
        .replace(/ ?\n ?/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim());
}
/** Same as normalizeText but folds every line break into a single space. */
export function normalizeSingleLine(input) {
    return normalizeText(input).replace(/\n+/g, ' ').replace(/ {2,}/g, ' ').trim();
}
/**
 * Backwards compatible alias kept for call sites that only handle single line
 * values (names, phone numbers, option keys).
 */
export const normalizeWhitespace = normalizeSingleLine;
export function isValidEmail(value) {
    return value.length <= LIMITS.email && EMAIL_RE.test(value);
}
export function isValidPhone(value) {
    if (!PHONE_ALLOWED_RE.test(value))
        return false;
    const digits = value.replace(/\D/g, '');
    return digits.length >= 5 && digits.length <= 20;
}
export function isValidIsoDate(value) {
    if (!ISO_DATE_RE.test(value))
        return false;
    const d = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
/**
 * Resolves relative date bounds used in field validation:
 * `today`, `today+30d`, `today-7d` or a plain ISO date.
 */
export function resolveDateBound(bound, now = new Date()) {
    if (ISO_DATE_RE.test(bound))
        return bound;
    const m = /^today(?:([+-])(\d+)d)?$/.exec(bound.trim());
    if (!m)
        return null;
    const base = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    if (m[1] && m[2]) {
        const delta = Number(m[2]) * (m[1] === '-' ? -1 : 1);
        base.setUTCDate(base.getUTCDate() + delta);
    }
    return base.toISOString().slice(0, 10);
}
/**
 * Compiles a field pattern safely. Patterns are anchored and rejected when they
 * contain constructs prone to catastrophic backtracking, so an admin cannot
 * (accidentally or otherwise) turn a form into a CPU denial of service.
 */
export function compileFieldPattern(pattern) {
    if (pattern.length > 200)
        return null;
    if (/\\[0-9]/.test(pattern))
        return null; // backreferences
    if (/\(\?</.test(pattern))
        return null; // lookbehind
    if (/(\([^)]*[+*][^)]*\))[+*]/.test(pattern))
        return null; // nested quantifiers
    try {
        const body = pattern.replace(/^\^/, '').replace(/\$$/, '');
        return new RegExp(`^(?:${body})$`, 'u');
    }
    catch {
        return null;
    }
}
/** Options of a field, either inline or resolved from a managed list. */
export function resolveOptions(field, lists = {}) {
    const source = field.listKey ? lists[field.listKey]?.items : field.options;
    return (source ?? []).filter((o) => o.active).sort((a, b) => a.order - b.order);
}
function flowValue(field, flow) {
    if (!flow)
        return undefined;
    if (field === '@reason')
        return flow.reason;
    if (field === '@appointment')
        return flow.hasAppointment ? 'yes' : 'no';
    return undefined;
}
/**
 * A field is skipped when it is hidden, disabled or when its `showWhen`
 * dependency is not satisfied by the submitted answers or the flow context.
 */
export function isFieldActive(field, answers, flow) {
    if (field.visibility !== 'visible')
        return false;
    if (!field.showWhen)
        return true;
    const source = field.showWhen.field;
    const dep = source.startsWith('@') ? flowValue(source, flow) : answers[source];
    if (dep === undefined || dep === null)
        return false;
    const values = Array.isArray(dep) ? dep.map(String) : [String(dep)];
    return values.some((v) => field.showWhen.equals.includes(v));
}
/** True for fields that only display something and never take an answer. */
export function isDisplayOnly(field) {
    return DISPLAY_ONLY_FIELD_TYPES.includes(field.type);
}
/** Fields the visitor actually sees, in display order. */
export function activeFields(form, answers, flow) {
    return [...form.fields].sort((a, b) => a.order - b.order).filter((f) => isFieldActive(f, answers, flow));
}
/* ---------------------------------------------------------------- validation */
function err(field, code, message) {
    return { field, code, message };
}
function validateScalar(field, raw, options, now) {
    const errors = [];
    const v = field.validation ?? {};
    switch (field.type) {
        case 'checkbox': {
            const value = raw === true || raw === 'true' || raw === 1 || raw === '1';
            if (field.required && !value)
                errors.push(err(field.name, 'required', `${field.label} is required`));
            return { errors, value };
        }
        case 'number': {
            if (raw === '' || raw === null || raw === undefined)
                return { errors, value: null };
            const num = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
            if (!Number.isFinite(num)) {
                return {
                    errors: [err(field.name, 'invalid_number', `${field.label} must be a number`)],
                    value: null,
                };
            }
            const min = v.min ?? LIMITS.numberMin;
            const max = v.max ?? LIMITS.numberMax;
            if (num < min || num > max) {
                errors.push(err(field.name, 'out_of_range', `${field.label} must be between ${min} and ${max}`));
            }
            return { errors, value: num };
        }
        case 'date': {
            const value = normalizeWhitespace(String(raw));
            if (value === '')
                return { errors, value: null };
            if (!isValidIsoDate(value)) {
                return {
                    errors: [err(field.name, 'invalid_date', `${field.label} must be a valid date`)],
                    value: null,
                };
            }
            const min = v.minDate ? resolveDateBound(v.minDate, now) : null;
            const max = v.maxDate ? resolveDateBound(v.maxDate, now) : null;
            if (min && value < min)
                errors.push(err(field.name, 'out_of_range', `${field.label} must not be before ${min}`));
            if (max && value > max)
                errors.push(err(field.name, 'out_of_range', `${field.label} must not be after ${max}`));
            return { errors, value };
        }
        case 'multiselect': {
            const list = Array.isArray(raw)
                ? raw
                : raw === null || raw === undefined || raw === ''
                    ? []
                    : [String(raw)];
            const cleaned = list.map((x) => normalizeWhitespace(String(x))).filter((x) => x !== '');
            if (cleaned.length > LIMITS.multiselectItems) {
                return {
                    errors: [
                        err(field.name, 'too_many_items', `${field.label} allows at most ${LIMITS.multiselectItems} entries`),
                    ],
                    value: [],
                };
            }
            const allowed = new Set(options.map((o) => o.value));
            const invalid = cleaned.filter((x) => !allowed.has(x));
            if (invalid.length > 0) {
                errors.push(err(field.name, 'not_an_option', `${field.label} contains an unknown option`));
            }
            if (field.required && cleaned.length === 0) {
                errors.push(err(field.name, 'required', `${field.label} is required`));
            }
            return { errors, value: cleaned.filter((x) => allowed.has(x)) };
        }
        case 'select':
        case 'radio':
        case 'search_select':
        case 'list_ref': {
            const value = normalizeWhitespace(String(raw ?? ''));
            if (value === '') {
                if (field.required)
                    errors.push(err(field.name, 'required', `${field.label} is required`));
                return { errors, value: null };
            }
            if (value.length > LIMITS.option) {
                return { errors: [err(field.name, 'too_long', `${field.label} is too long`)], value: null };
            }
            if (!options.some((o) => o.value === value)) {
                return {
                    errors: [err(field.name, 'not_an_option', `${field.label} contains an unknown option`)],
                    value: null,
                };
            }
            return { errors, value };
        }
        case 'email': {
            const value = normalizeWhitespace(String(raw ?? '')).toLowerCase();
            if (value === '') {
                if (field.required)
                    errors.push(err(field.name, 'required', `${field.label} is required`));
                return { errors, value: null };
            }
            if (!isValidEmail(value)) {
                return {
                    errors: [err(field.name, 'invalid_email', `${field.label} is not a valid e-mail address`)],
                    value: null,
                };
            }
            return { errors, value };
        }
        case 'phone': {
            const value = normalizeWhitespace(String(raw ?? ''));
            if (value === '') {
                if (field.required)
                    errors.push(err(field.name, 'required', `${field.label} is required`));
                return { errors, value: null };
            }
            if (value.length > LIMITS.phone || !isValidPhone(value)) {
                return {
                    errors: [err(field.name, 'invalid_phone', `${field.label} is not a valid phone number`)],
                    value: null,
                };
            }
            return { errors, value };
        }
        case 'text':
        case 'textarea':
        default: {
            const max = Math.min(v.maxLength ?? Infinity, field.type === 'textarea' ? LIMITS.textarea : LIMITS.text);
            const value = field.type === 'textarea' ? normalizeText(String(raw ?? '')) : normalizeSingleLine(String(raw ?? ''));
            if (value === '') {
                if (field.required)
                    errors.push(err(field.name, 'required', `${field.label} is required`));
                return { errors, value: null };
            }
            if (value.length > max) {
                return {
                    errors: [err(field.name, 'too_long', `${field.label} must be at most ${max} characters`)],
                    value: null,
                };
            }
            if (v.minLength !== undefined && value.length < v.minLength) {
                errors.push(err(field.name, 'too_short', `${field.label} must be at least ${v.minLength} characters`));
            }
            if (v.pattern) {
                const re = compileFieldPattern(v.pattern);
                if (re && !re.test(value)) {
                    errors.push(err(field.name, 'pattern', `${field.label} has an invalid format`));
                }
            }
            return { errors, value };
        }
    }
}
/**
 * Validates a submitted answer map against a form definition.
 *
 * This is the single source of truth for form validation and runs on the server
 * for every check-in. The kiosk uses the very same function for inline feedback,
 * which keeps client and server behaviour identical by construction.
 */
export function validateSubmission(form, input, options = {}) {
    const lists = options.lists ?? {};
    const now = options.now ?? new Date();
    const errors = [];
    const values = {};
    const byName = new Map(form.fields.map((f) => [f.name, f]));
    if (options.rejectUnknown) {
        for (const key of Object.keys(input)) {
            if (!byName.has(key)) {
                errors.push(err(key, 'unknown_field', `Unknown field "${key}"`));
            }
        }
    }
    // Conditional visibility is resolved against the raw input so that a field
    // controlling another field is evaluated before its dependents.
    for (const field of [...form.fields].sort((a, b) => a.order - b.order)) {
        if (!isFieldActive(field, input, options.flow) || isDisplayOnly(field))
            continue;
        const fieldOptions = resolveOptions(field, lists);
        const raw = input[field.name];
        if (raw === undefined) {
            if (field.required && field.type !== 'checkbox') {
                errors.push(err(field.name, 'required', `${field.label} is required`));
            }
            else if (field.type === 'checkbox' && field.required) {
                errors.push(err(field.name, 'required', `${field.label} is required`));
            }
            continue;
        }
        const res = validateScalar(field, raw, fieldOptions, now);
        errors.push(...res.errors);
        if (res.value !== null && res.value !== undefined)
            values[field.name] = res.value;
    }
    return { ok: errors.length === 0, errors, values };
}
const FIELD_NAME_RE = /^[a-z][a-z0-9_]{0,48}$/;
/**
 * Validates a form definition itself (used when an admin saves the builder).
 * Prevents duplicate names, dangling `showWhen` references, circular
 * dependencies and unsafe validation patterns from ever being persisted.
 */
export function validateFormDefinition(fields) {
    const issues = [];
    if (fields.length > LIMITS.fieldsPerForm) {
        issues.push({ path: 'fields', message: `A form may not have more than ${LIMITS.fieldsPerForm} fields` });
    }
    const seen = new Set();
    for (const [i, f] of fields.entries()) {
        const p = `fields[${i}]`;
        if (!FIELD_NAME_RE.test(f.name)) {
            issues.push({
                path: `${p}.name`,
                message: 'name must be lowercase, start with a letter and contain only a-z, 0-9 and _',
            });
        }
        if (seen.has(f.name))
            issues.push({ path: `${p}.name`, message: `duplicate field name "${f.name}"` });
        seen.add(f.name);
        if (!f.label || f.label.trim() === '') {
            issues.push({ path: `${p}.label`, message: 'label is required' });
        }
        const needsOptions = ['select', 'radio', 'multiselect', 'search_select', 'list_ref'].includes(f.type);
        if (isDisplayOnly(f) && f.required) {
            issues.push({ path: `${p}.required`, message: `field type "${f.type}" cannot be required` });
        }
        if (needsOptions && !f.listKey && (!f.options || f.options.length === 0)) {
            issues.push({ path: `${p}.options`, message: `field type "${f.type}" needs options or a listKey` });
        }
        if (f.validation?.pattern && compileFieldPattern(f.validation.pattern) === null) {
            issues.push({ path: `${p}.validation.pattern`, message: 'pattern is invalid or potentially unsafe' });
        }
        if (f.validation?.minDate && resolveDateBound(f.validation.minDate, new Date()) === null) {
            issues.push({ path: `${p}.validation.minDate`, message: 'minDate must be an ISO date or today[+-]Nd' });
        }
        if (f.validation?.maxDate && resolveDateBound(f.validation.maxDate, new Date()) === null) {
            issues.push({ path: `${p}.validation.maxDate`, message: 'maxDate must be an ISO date or today[+-]Nd' });
        }
    }
    // showWhen references must exist and must not form a cycle.
    const deps = new Map();
    for (const [i, f] of fields.entries()) {
        if (!f.showWhen)
            continue;
        if (f.showWhen.equals.length === 0) {
            issues.push({ path: `fields[${i}].showWhen.equals`, message: 'choose at least one value' });
        }
        if (f.showWhen.field.startsWith('@')) {
            if (!FLOW_CONDITION_FIELDS.includes(f.showWhen.field)) {
                issues.push({
                    path: `fields[${i}].showWhen.field`,
                    message: `references unknown condition "${f.showWhen.field}"`,
                });
            }
            continue;
        }
        if (!seen.has(f.showWhen.field)) {
            issues.push({
                path: `fields[${i}].showWhen.field`,
                message: `references unknown field "${f.showWhen.field}"`,
            });
            continue;
        }
        if (f.showWhen.field === f.name) {
            issues.push({ path: `fields[${i}].showWhen.field`, message: 'a field cannot depend on itself' });
            continue;
        }
        deps.set(f.name, f.showWhen.field);
    }
    for (const start of deps.keys()) {
        const visited = new Set([start]);
        let cur = deps.get(start);
        while (cur) {
            if (visited.has(cur)) {
                issues.push({ path: 'fields', message: `circular showWhen dependency involving "${start}"` });
                break;
            }
            visited.add(cur);
            cur = deps.get(cur);
        }
    }
    return issues;
}
/**
 * Renders answers for display / notification, resolving option values to their
 * labels. Historic submissions keep working even after the form changed because
 * unknown keys fall back to their raw value.
 */
export function labelForAnswer(field, value, lists = {}, language) {
    if (value === null || value === undefined)
        return '';
    if (!field)
        return Array.isArray(value) ? value.join(', ') : String(value);
    const options = resolveOptions(field, lists);
    const toLabel = (v) => {
        const opt = options.find((o) => o.value === v);
        if (!opt)
            return v;
        return (language && opt.labelI18n?.[language]) || opt.label;
    };
    if (Array.isArray(value))
        return value.map(toLabel).join(', ');
    if (typeof value === 'boolean')
        return value ? 'Ja' : 'Nein';
    if (typeof value === 'number')
        return String(value);
    return toLabel(value);
}
//# sourceMappingURL=engine.js.map